import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid, MenuItem, SelectChangeEvent, Tooltip, TooltipProps, styled } from "@mui/material";
import { tooltipClasses } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Color } from "../../../../../../../../components/Form/Button";
import Checkbox from "../../../../../../../../components/Form/Checkbox";
import Input from "../../../../../../../../components/Form/Input";
import InputRange from "../../../../../../../../components/Form/InputRange";
import Select from "../../../../../../../../components/Form/Select";
import Modal from "../../../../../../../../components/Generic/Modal";
import { LIFTVAN } from "../../../../../../../../constants";
import { useAppState } from "../../../../../../../../context/StateContext";
import { useOrderContext } from "../../../../../../../../hooks/useOrderContext";
import { useStorageBoxHook } from "../../../../../../../../hooks/useStorageBoxActions";
import useYupValidation from "../../../../../../../../hooks/useYupValidation";
import { LiftvanConfig, defaultLiftvanConfig } from "../../../../../../../../models/liftvan.config";
import { Order } from "../../../../../../../../models/order";
import { StorageBoxDto } from "../../../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import { Transshipment } from "../../../../../../../../models/transshipment";
import LiftvanConfigService from "../../../../../../../../services/liftvan.config.service";
import StorageBoxTypeService from "../../../../../../../../services/storage.box.type.service";
import { handleGetConvertValue } from "../../../../../../../../util/converts";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";
import useStyles, { CheckboxContainer, LiftvanGridContent, LiftVanInfoIcon, Title } from "./styles";

interface SingleUseBoxModalProps {
  job?: Order;
  transshipment?: Transshipment;
  showLeftActionButtons?: boolean;
  onSubmit?: (storageBox: StorageBoxDto) => void;
  onSubmitLiftvan?: (storageBox: StorageBoxDto, liftvanConfig: LiftvanConfig) => void;
  storageBoxTypesList?: StorageBoxType[];
  isTransshipment?: boolean;
}

export interface SingleUseBoxModalHandle {
  close: () => void;
  open: (storageBoxType?: StorageBoxType) => void;
}

const SingleUseBoxModal: ForwardRefRenderFunction<
  SingleUseBoxModalHandle,
  SingleUseBoxModalProps
> = (props, ref) => {
  const {
    job,
    transshipment,
    showLeftActionButtons = true,
    onSubmit,
    onSubmitLiftvan,
    storageBoxTypesList = [],
    isTransshipment = false,
  } = props;

  const classes = useStyles();

  const { useCreateStorageBox, useCreateLiftvanConfig, useUpdateLiftvanConfig } =
    useStorageBoxHook();

  const { refetchStorageBoxes } = useOrderContext();

  const { currentUser } = useAppState();

  const { t: translate } = useTranslation();

  const [capacity, setCapacity] = useState<string | number>();
  const [createAnotherSingleUseBox, setCreateAnotherSingleUseBox] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [liftvanConfig, setLiftvanConfig] = useState<LiftvanConfig>({
    ...defaultLiftvanConfig,
    jobId: job?.id,
  });
  const [open, setOpen] = useState<boolean>(false);
  const [storageBoxNumber, setStorageBoxNumber] = useState<string>("");
  const [storageBoxType, setStorageBoxType] = useState<StorageBoxType>();
  const [storageBoxTypes, setStorageBoxTypes] = useState<StorageBoxType[]>(
    storageBoxTypesList || []
  );

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  // eslint-disable-next-line global-require
  const INFO_CIRCLE_ICON = require("../../../../../../../../assets/icons/info-circle.svg");

  const clearForm = () => {
    setCapacity(0);
    setCreateAnotherSingleUseBox(false);
    setStorageBoxNumber("");
    setStorageBoxType(undefined);
  };

  const onCloseModal = () => {
    clearForm();
    setOpen(false);
  };

  const onOpenModal = (storageBoxType?: StorageBoxType) => {
    setStorageBoxType(storageBoxType);
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({ open: onOpenModal, close: onCloseModal }));

  const storageBoxSchema = yup.object().shape({
    storageBoxNumber: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    storageBoxTypeId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const liftvanConfigSchema = yup.object().shape({
    prefix: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    howMany: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const onCreateAnotherSBOrCloseModal = () => {
    if (createAnotherSingleUseBox) {
      setStorageBoxNumber("");

      return;
    }

    onCloseModal();
  };

  const { mutate: createStorageBox, isLoading: isCreatingSingleUseBox } = useCreateStorageBox(
    () => {
      refetchStorageBoxes();
      onCreateAnotherSBOrCloseModal();
    }
  );

  const { mutate: createLiftvanConfig, isLoading: isCreatingLiftvanConfig } =
    useCreateLiftvanConfig(() => {
      refetchStorageBoxes();
      onCreateAnotherSBOrCloseModal();
    });

  const { mutate: updateLiftvanConfig, isLoading: isUpdatingLiftvanConfig } =
    useUpdateLiftvanConfig(() => {
      refetchStorageBoxes();
      onCreateAnotherSBOrCloseModal();
    });

  useEffect(() => {
    if (storageBoxType?.name === LIFTVAN) {
      if (isTransshipment) {
        setLiftvanConfig({
          ...defaultLiftvanConfig,
          capacity: storageBoxType?.capacity || 0,
        });
      } else {
        setLiftvanConfig((state) => ({
          ...(state || {}),
          capacity: liftvanConfig.capacity <= 0 ? storageBoxType?.capacity || 0 : state.capacity,
        }));
      }
    }
    setStorageBoxNumber("");
  }, [storageBoxType]);

  const storageBoxesFields = { storageBoxNumber, storageBoxTypeId: storageBoxType?.id };
  const liftvanConfigFields = { howMany: liftvanConfig?.howMany, prefix: liftvanConfig?.prefix };

  const isValid = (schema: yup.ObjectSchema<any>, fields: any) => {
    try {
      schema.validateSync(fields, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const { isFetching: isFetchingStorageBoxTypes } = useQuery(
    ["storage.box.types.list", currentUser?.companyId],
    () =>
      StorageBoxTypeService.getAll({
        companyId: currentUser?.companyId,
        include: JSON.stringify({
          association: "storageBoxes",
          attributes: ["id"],
          limit: 1,
          where: { isActive: true, singleUse: false },
        }),
        isActive: "1",
        sortBy: JSON.stringify([["name"]]),
      }),
    {
      enabled: !!currentUser?.companyId && (!storageBoxTypesList || !storageBoxTypesList?.length),
      onSuccess: (response) => {
        if (response && response.data) {
          setStorageBoxTypes(response.data.filter((type) => type.storageBoxes?.length === 0));
        }
      },
    }
  );

  const { isFetching: isFetchingLiftvanConfig } = useQuery(
    ["liftvan.config.get", job?.id],
    () => LiftvanConfigService.getByJobId(job?.id ?? 0),
    {
      enabled: !!job?.id,
      onSuccess: (response) => {
        if (response && response.data) {
          setLiftvanConfig(response.data);
        }
      },
    }
  );

  useEffect(() => {
    setCapacity(
      handleGetConvertValue(
        storageBoxType?.capacity || 0,
        currentUser?.measurement?.code || currentUser?.company?.measurement?.code || "m³"
      )
    );
  }, [storageBoxType]);

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (storageBoxType?.name === LIFTVAN) {
      if (isValid(liftvanConfigSchema, liftvanConfigFields)) {
        if (onSubmitLiftvan) {
          const storageBoxToSave: StorageBoxDto = {
            number: storageBoxNumber,
            singleUse: true,
            singleUseJobId: job?.id,
            storageBoxTypeId: storageBoxType?.id ?? 0,
          };

          onSubmitLiftvan(storageBoxToSave, liftvanConfig);
          onCloseModal();
        } else if (liftvanConfig.id) updateLiftvanConfig(liftvanConfig);
        else createLiftvanConfig(liftvanConfig);
      }
    } else {
      const storageBoxToSave: StorageBoxDto = {
        number: storageBoxNumber,
        singleUse: true,
        singleUseJobId: job?.id,
        storageBoxTypeId: storageBoxType?.id ?? 0,
      };

      if (isValid(storageBoxSchema, storageBoxesFields)) {
        if (onSubmit) {
          onSubmit(storageBoxToSave);
          onCloseModal();
        } else {
          createStorageBox(storageBoxToSave);
        }
      }
    }
  };

  useEffect(() => {
    if (liftvanConfig.autoNumber)
      setLiftvanConfig({
        ...liftvanConfig,
        prefix: isTransshipment ? transshipment?.reference || "" : job?.reference,
      });
    else setLiftvanConfig({ ...liftvanConfig, prefix: undefined });
  }, [liftvanConfig.autoNumber]);

  const AutoNumberInfoTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      padding: "4px 8px",
      maxWidth: 200,
      background: colors.gray600,
      borderRadius: "4px",
      font: "400 12px Manrope",
      color: colors.white,
      lineHeight: "16px",
      letterSpacing: "0.4px",
    },
  }));

  return (
    <Modal
      actionButtonProps={{
        customColorSave: Color.SUCCESS,
        customColorCancel: Color.TEXT_INFO,
        loading: isCreatingSingleUseBox || isCreatingLiftvanConfig || isUpdatingLiftvanConfig,
        onCancel: () => onCloseModal(),
        onSave: () => handleSubmit(),
        saveDisabled: isCreatingSingleUseBox || isCreatingLiftvanConfig || isUpdatingLiftvanConfig,
        textSave:
          storageBoxType?.name === LIFTVAN && liftvanConfig.id
            ? translate("save")
            : translate("add"),
      }}
      customLeftButtons={
        storageBoxType?.name === LIFTVAN ? null : (
          <Checkbox
            label={translate("createAnotherSingleUseBox")}
            checked={createAnotherSingleUseBox}
            onChange={(_, checked) => setCreateAnotherSingleUseBox(checked)}
          />
        )
      }
      handleClose={() => onCloseModal()}
      open={open}
      showActionButtons
      showLeftActionButtons={showLeftActionButtons}
      title={translate("addStorageBox")}
      className={classes.modalHeader}
    >
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={12}>
          <Title>{translate("newSingleUseBox")}</Title>
        </Grid>
        <Grid item xs={12} md={5}>
          <Select
            data-cy="storageBoxType"
            disabled={isFetchingStorageBoxTypes}
            error={fieldHasError("storageBoxTypeId")}
            errorMessage={getErrorByField("storageBoxTypeId")}
            label={translate("type")}
            onChange={(event: SelectChangeEvent<any>) => {
              const id = Number(event.target.value);
              const type = storageBoxTypes.find((type) => type.id === id);

              setStorageBoxType(type);
            }}
            placeholder={`${translate("select")}...`}
            required
            value={storageBoxType?.id}
          >
            {storageBoxTypes?.map((storageBoxType) => (
              <MenuItem value={storageBoxType?.id} key={storageBoxType?.id}>
                {storageBoxType?.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        {storageBoxType?.name === LIFTVAN ? (
          <>
            <Grid item xs={12} md={2}>
              <InputRange
                change={(value) => setLiftvanConfig({ ...liftvanConfig, howMany: Number(value) })}
                containerClassName={classes.containerInputRange}
                data-cy="howMany"
                disabled={isFetchingLiftvanConfig}
                error={fieldHasError("howMany")}
                errorMessage={getErrorByField("howMany")}
                label={translate("howMany")}
                minValue={defaultLiftvanConfig.howMany}
                required
                value={liftvanConfig?.howMany}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <CheckboxContainer>
                <Checkbox
                  checked={liftvanConfig?.autoNumber}
                  disabled={isFetchingLiftvanConfig}
                  label={translate("autoNumberThem")}
                  onChange={(_, checked) =>
                    setLiftvanConfig({ ...liftvanConfig, autoNumber: checked })
                  }
                />
                {isTransshipment && (
                  <AutoNumberInfoTooltip
                    title={translate(
                      "createsANomenclatureBasedOnTheTransshipmentNumberAndTheQuantityOfLiftvans"
                    )}
                    placement="top"
                  >
                    <LiftVanInfoIcon
                      src={INFO_CIRCLE_ICON}
                      width="12"
                      height="12"
                      alt="info icon"
                    />
                  </AutoNumberInfoTooltip>
                )}
              </CheckboxContainer>
            </Grid>
            <LiftvanGridContent container item xs={12} md={5} spacing={2}>
              <Grid item xs={12} md={9}>
                <Input
                  data-cy="liftvanConfig.prefix"
                  disabled={liftvanConfig?.autoNumber || isFetchingLiftvanConfig}
                  error={fieldHasError("prefix")}
                  errorMessage={getErrorByField("prefix")}
                  label={translate("prefix")}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setLiftvanConfig({ ...liftvanConfig, prefix: event.target.value })
                  }
                  placeholder={`${translate("typePrefix")}...`}
                  required
                  value={liftvanConfig.prefix}
                />
              </Grid>
              <LiftvanGridContent item xs={12} md={3}>
                <Input
                  disabled
                  inputTextAlign="center"
                  label={"\u00A0"}
                  value={
                    liftvanConfig?.howMany
                      ? `1 ${translate("of")} ${liftvanConfig?.howMany}`
                      : "\u00A0"
                  }
                />
              </LiftvanGridContent>
            </LiftvanGridContent>
            <Grid container item direction="row" alignItems="flex-start" spacing={2}>
              <Grid item xs={12} md={5}>
                <Input
                  data-cy="liftvanConfig.capacity"
                  disabled={isFetchingLiftvanConfig}
                  placeholder={"-"}
                  label={`${translate("capacityOfEachLiftvan")} (${getMeasurement()?.code})`}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setLiftvanConfig({ ...liftvanConfig, capacity: Number(event.target.value) })
                  }
                  type="number"
                  value={liftvanConfig?.capacity}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={5}>
              <Input
                data-cy="storageBoxNumber"
                error={fieldHasError("storageBoxNumber")}
                errorMessage={getErrorByField("storageBoxNumber")}
                label={translate("prefix")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setStorageBoxNumber(event.target.value)
                }
                placeholder={`${translate("typeNumber")}...`}
                required
                value={storageBoxNumber}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Input
                data-cy="storageBoxTypeCapacity"
                placeholder={"-"}
                label={translate("capacity")}
                value={`${capacity} ${getMeasurement()?.code}`}
                disabled
              />
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  );
};

export default forwardRef(SingleUseBoxModal);
